/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentChildren, ComponentProps, Ref } from 'preact';
import { forwardRef, useImperativeHandle, useRef } from 'preact/compat';

import { FocusableHandle } from '../hooks/UNSAFE_useFocusableTextField';
import { useCollectionFocusRing } from '../hooks/PRIVATE_useCollectionFocusRing';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import { FlexboxProps } from '../utils/UNSAFE_interpolations/flexbox';
import { Size } from '../utils/UNSAFE_size';
import { BundleType } from '../resources/nls/bundle';
import { AriaAttributesSignalExcluded } from '#utils/UNSAFE_attributeUtils';

import { Flex } from '../UNSAFE_Flex';
import { Label } from '../UNSAFE_Label';

import {
  InlineHelp,
  InlineUserAssistance,
  InlineUserAssistanceContainer,
  UserAssistanceDensityType
} from '../UNSAFE_UserAssistance';
import { useCheckboxRadioField } from './useCheckboxRadioField';
import { HiddenAccessible } from '../UNSAFE_HiddenAccessible';

import { xUnits } from '../UNSAFE_Theme';

import { topUAContainerStyles } from './CheckboxRadioFieldStyles.css';
import { LabelValueLayout } from '../UNSAFE_LabelValueLayout';
import { CheckboxRadioContext } from './CheckboxRadioContext';
import { layoutSpanStyles, LayoutColumnSpan } from '../utils/UNSAFE_styles/Layout';
import { type TestIdProps, useTestId } from '#hooks/UNSAFE_useTestId';

type InlineUserAssistanceProps = ComponentProps<typeof InlineUserAssistance>;

type AriaProps = Pick<AriaAttributesSignalExcluded, 'aria-describedby'>;

type Props = TestIdProps &
  AriaProps & {
    /**
     * Text to provide guidance to help the user understand what data to enter.
     */
    assistiveText?: InlineUserAssistanceProps['assistiveText'];
    /**
     * baseId, used to generate other element IDs.
     */
    baseId: string;
    /**
     * <RadioItem /> or <CheckboxItem />
     */
    children: ComponentChildren;
    /**
     * Specifies how many columns to span in a FormLayout with direction === 'row'
     */
    columnSpan?: LayoutColumnSpan;
    /**
     * layout direction of the children elements
     */
    direction?: FlexboxProps['direction'];
    /**
     * Value of the radio option.
     */
    hasValue?: boolean;
    /**
     * Help source URL associated with the component.
     */
    helpSourceLink?: InlineUserAssistanceProps['helpSourceLink'];
    /**
     * Custom text to be rendered for the <code>helpSourceLink</code>.
     */
    helpSourceText?: InlineUserAssistanceProps['helpSourceText'];
    /**
     * Specifies if the radio group is disabled.
     */
    isDisabled?: boolean;
    /**
     * Specifies if the radio group is read-only.
     */
    isReadonly?: boolean;
    /**
     * Specifies whether the radio group requires a selection to be made.
     */
    isRequired?: boolean;
    /**
     * Label text for the group of radio buttons.
     */
    label: string;
    /**
     * Specifies where the label is positioned relative to the field.
     */
    labelEdge?: 'none' | 'top' | 'start' | 'inside';
    /**
     * Specifies the width of the label when <code>labelEdge</code> is <code>"start"</code>.
     */
    labelStartWidth?: Size;
    /**
     * Messages to show on screen that are associated with the component.
     */
    messages?: InlineUserAssistanceProps['messages'];

    role?: 'radiogroup' | 'group';
    /**
     * Specifies the density of the user assistance presentation.  It can be set to:
     * <ul>
     * <li><code>'efficient'</code>: Show inline and reserve space to prevent layout reflow when user
     * assistance text is displayed.</li>
     * <li><code>'reflow'</code>: Show inline.  Layout will reflow when text is displayed.</li>
     * <li><code>'compact'</code>: Messages, help, hints, and required indicator will be shown inline just as in reflow (default) mode. </li>
     * </ul>
     */
    userAssistanceDensity?: UserAssistanceDensityType;

    parentComponentVariant: 'radioSet' | 'checkboxSet';
  };

export const CheckboxRadioField = forwardRef(
  (
    {
      'aria-describedby': ariaDescribedBy,
      assistiveText,
      baseId,
      direction = 'column',
      columnSpan = 1,
      hasValue,
      helpSourceLink,
      helpSourceText,
      isDisabled,
      isReadonly,
      isRequired,
      label,
      labelEdge,
      labelStartWidth = '33%',
      parentComponentVariant,
      messages,
      userAssistanceDensity,
      role,
      children,
      testId
    }: Props,
    ref: Ref<FocusableHandle> = null
  ) => {
    const testIdProps = useTestId(testId);
    const hasTopUserAssistance =
      !isDisabled && !isReadonly && (!!assistiveText || (!!helpSourceLink && !!helpSourceText));
    const hasBottomUserAssistance = !isDisabled && !isReadonly;
    const groupRef = useRef<HTMLDivElement>(null);

    const {
      groupProps,
      labelProps,
      topUserAssistanceProps,
      bottomUserAssistanceProps,
      readonlyId
    } = useCheckboxRadioField({
      ariaDescribedBy,
      baseId,
      isRequired,
      isReadonly,
      isDisabled,
      label,
      labelEdge,
      messages,
      hasValue,
      hasTopUserAssistance,
      hasBottomUserAssistance,
      parentComponentVariant
    });

    const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
    const readonlyLabel = translations.formControl_readOnly();
    const isRadioSet = parentComponentVariant === 'radioSet';

    useImperativeHandle(ref, () => ({
      focus: () => {
        if (!isDisabled) {
          const inputChildren = groupRef.current?.querySelectorAll<HTMLInputElement>('input') ?? [];
          const selectedChild = Array.from(inputChildren).find((child) => child.checked);

          if (selectedChild) {
            selectedChild.focus();
          } else {
            inputChildren[0].focus();
          }
        }
      },
      blur: () => {
        if (groupRef.current?.contains(document.activeElement)) {
          (document.activeElement as HTMLElement).blur();
        }
      }
    }));

    // the props from the hook are used for focus management on the radioset variant to allow for arrow navigation
    // while checkboxset uses tabbing
    const [isFocusRingShown, focusRingProps] = useCollectionFocusRing(
      groupRef,
      isRadioSet ? ['ArrowUp', 'ArrowRight', 'ArrowDown', 'ArrowLeft'] : ['Tab']
    );

    const content = (
      <>
        {hasTopUserAssistance && (
          <div
            id={`${baseId}-top-ua-container`}
            class={labelEdge === 'start' ? topUAContainerStyles.start : topUAContainerStyles.top}>
            <InlineUserAssistanceContainer {...topUserAssistanceProps}>
              <InlineHelp
                assistiveText={assistiveText}
                sourceLink={helpSourceLink}
                sourceText={helpSourceText}
              />
            </InlineUserAssistanceContainer>
          </div>
        )}
        <Flex
          direction={direction}
          {...(direction === 'row' && { gap: [0, xUnits(8) as Size], wrap: 'wrap' })}>
          <CheckboxRadioContext.Provider value={{ isFocusRingShown }}>
            {children}
          </CheckboxRadioContext.Provider>
        </Flex>
        {hasBottomUserAssistance && !isReadonly && (
          <InlineUserAssistance
            {...bottomUserAssistanceProps}
            requiredAlignment="start"
            messages={messages}
            userAssistanceDensity={
              userAssistanceDensity === 'reflow' ? userAssistanceDensity : 'efficient'
            }
          />
        )}
      </>
    );
    // The top level element needs the column span class on it.
    const classes = layoutSpanStyles.layoutSpanColumn[columnSpan];

    return (
      <div
        role={role}
        ref={groupRef}
        class={classes}
        {...groupProps}
        {...focusRingProps}
        {...testIdProps}>
        {labelEdge === 'none' && content}
        {labelEdge !== 'none' && (
          <LabelValueLayout
            parentComponentVariant={parentComponentVariant}
            hasTopUserAssistance={hasTopUserAssistance}
            label={
              <Label
                {...labelProps}
                variant={labelEdge}
                userAssistanceDensity="efficient"
                parentComponentVariant={parentComponentVariant}>
                {label}
              </Label>
            }
            labelEdge={labelEdge}
            labelStartWidth={labelStartWidth}>
            {content}
          </LabelValueLayout>
        )}
        {isReadonly && (
          <HiddenAccessible id={readonlyId} isHidden={true}>
            {readonlyLabel}
          </HiddenAccessible>
        )}
      </div>
    );
  }
);
